import { useState, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import CheckGlucose from './pages/CheckGlucose';
import InjectInsulin from './pages/InjectInsulin';
import InsulinSchedule from './pages/InsulinSchedule';
import ClinicalData from './pages/ClinicalData';
import Profile from './pages/Profile';
import Achievements from './pages/Achievements';
import ConsultationPhotos from './pages/ConsultationPhotos';
import Consent from './pages/Consent';
import MonthlyReport from './pages/MonthlyReport';
import HistoryListPage from './pages/HistoryList';
import Dashboard from './pages/Dashboard';
import About from './pages/About';
import Login from './pages/Login';
import Register from './pages/Register';
import CompleteProfile from './pages/CompleteProfile';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Header from './components/Header';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { InstallPromptModal, BeforeInstallPromptEvent } from './components/InstallPromptModal';

function AppShell() {
  const location = useLocation();
  const isRoot = location.pathname === '/' || location.pathname === '';
  const isAuthPage =
    location.pathname === '/login' ||
    location.pathname === '/register' ||
    location.pathname === '/complete-profile' ||
    // FR-22: the header's links lead back here until the question is
    // answered, so the consent screen stands alone.
    location.pathname === '/consent' ||
    location.pathname === '/forgot-password' ||
    location.pathname === '/reset-password';

  const [installPromptEvent, setInstallPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installDismissed, setInstallDismissed] = useState(() => {
    try {
      return localStorage.getItem('install_dismissed') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPromptEvent(event as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPromptEvent(null);
    };

    const matchMedia = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (evt: MediaQueryListEvent) => {
      if (evt.matches) {
        setIsInstalled(true);
      }
    };

    // Modern browsers
    try {
      matchMedia.addEventListener('change', handleDisplayModeChange);
    } catch {
      // Fallback for older browsers
      matchMedia.addListener(handleDisplayModeChange);
    }

    if (matchMedia.matches || (window.navigator as any).standalone === true) {
      setIsInstalled(true);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      try {
        matchMedia.removeEventListener('change', handleDisplayModeChange);
      } catch {
        matchMedia.removeListener(handleDisplayModeChange);
      }
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!installPromptEvent) {
      return;
    }
    try {
      await installPromptEvent.prompt();
      const choice = await installPromptEvent.userChoice;
      if (choice.outcome === 'accepted') {
        setInstallPromptEvent(null);
      }
    } catch {
      setInstallPromptEvent(null);
    }
  };

  const handleDismissInstall = () => {
    setInstallDismissed(true);
    try {
      localStorage.setItem('install_dismissed', 'true');
    } catch {}
  };

  // Show modal if not installed, not dismissed, and (we have an event OR we want to show instructions)
  // But usually we only show if we have an event OR if it's explicitly requested.
  // The requirement is "modal when first load App".
  // So we show it if !isInstalled && !installDismissed.
  const showInstallModal = !isInstalled && !installDismissed;

  return (
    <>
      {!isAuthPage && <Header title="Insulin Injection" enableBack={!isRoot} />}
      <main className="!mt-0">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          {/* Public: a locked-out user has no session to protect these with. */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/complete-profile"
            element={
              <ProtectedRoute requireProfile={false}>
                <CompleteProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />
          <Route
            path="/check-glucose"
            element={
              <ProtectedRoute>
                <CheckGlucose />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inject-insulin"
            element={
              <ProtectedRoute>
                <InjectInsulin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/insulin-schedule"
            element={
              <ProtectedRoute>
                <InsulinSchedule />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clinical-data"
            element={
              <ProtectedRoute>
                <ClinicalData />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          {/* FR-19. Patient-only: not in CLINICIAN_ROUTES, so a doctor is sent
              back to /dashboard like any other patient screen. */}
          <Route
            path="/prestasi"
            element={
              <ProtectedRoute>
                <Achievements />
              </ProtectedRoute>
            }
          />
          {/* FR-03 shipped its family-data screen here before FR-02's profile
              editor existed; it now lives on /profile. Kept as a redirect so
              an installed PWA's history or a bookmark still lands. */}
          <Route path="/family-data" element={<Navigate to="/profile" replace />} />
          <Route
            path="/consent"
            element={
              <ProtectedRoute>
                <Consent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/consultation-photos"
            element={
              <ProtectedRoute>
                <ConsultationPhotos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/monthly-report"
            element={
              <ProtectedRoute>
                <MonthlyReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <HistoryListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/about"
            element={
              <ProtectedRoute>
                <About />
              </ProtectedRoute>
            }
          />
          {/* The SPA fallback in public/.htaccess serves index.html for every
              unknown path, so React Router — not the web server — is what turns
              a bad URL into something sensible. Without this the user gets a
              blank page. See docs/defects.md#df-01. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <InstallPromptModal
        isVisible={showInstallModal}
        installPromptEvent={installPromptEvent}
        onInstall={handleInstallClick}
        onDismiss={handleDismissInstall}
      />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}
